/**
 * 회원 식별 + 가족 + 여권 통합 컨테이너 훅
 * docs/tools_all.md §11-3 (v8 — 단일 boolean dirty)
 *
 * 책임:
 *  - identifyByQR / identifyByPhone / identifyByTicket 진입점 (단일 mutex identifyLock)
 *  - scannedMember vs primary(PARENT) 분리 (§5-3-a)
 *  - family + passportMap 병렬 로딩
 *  - hasUnacknowledgedChanges 단일 boolean + markDirty/acknowledgeAndReset (§5-3-c, §11-3)
 *  - pendingActions Set + beginAction/endAction (in-flight 가드)
 *
 * 후속 PR 에서 확장:
 *  - PR3: refresh, addChild/updateChild/removeChild 위임
 *  - PR6: bank-session markDirty 연결, 노란 배너/모달
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  getMemberByQR,
  getMemberByPhone,
  getMemberByTicket,
  getFamilyMembers,
  getAllMemberPassports,
} from '../services/api';
import { extractQRCode } from '../utils/qrUtils';

// JRG_ = 회원 QR, ZLB_ = QR밴드. 밴드도 /member/by-qr 로 조회한다
// (tools 는 인증 경로라 서버가 allow_band=True — member.py §by-qr)
const isOurQR = (code) =>
  typeof code === 'string' && (code.startsWith('JRG_') || code.startsWith('ZLB_'));

async function loadFamilyAndPassports(member) {
  let family = [];
  let primary = null;
  try {
    const familyRes = await getFamilyMembers(member.member_id);
    if (familyRes?.parent) {
      const children = (familyRes.children || []).map((c) => ({ ...c, member_type: 'CHILD' }));
      family = [familyRes.parent, ...children];
      primary = familyRes.parent;
    }
  } catch (_) {
    // SINGLE 회원 또는 가족 조회 실패 — family=[], primary=null 유지
    family = [];
    primary = null;
  }

  const targets = family.length ? family : [member];
  const passportMap = {};
  await Promise.all(
    targets.map(async (m) => {
      try {
        const passRes = await getAllMemberPassports(m.member_id, false);
        const passports = passRes?.passports || passRes?.data?.passports || [];
        passportMap[m.member_id] = passports.find((p) => p.status === 'ACTIVE' && p.is_active) || null;
      } catch (_) {
        passportMap[m.member_id] = null;
      }
    }),
  );

  return { family, primary, passportMap };
}

export default function useMemberIdentity() {
  const [scannedMember, setScannedMember] = useState(null);
  const [primary, setPrimary] = useState(null);
  const [family, setFamily] = useState([]);
  const [passportMap, setPassportMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ticketNoMember, setTicketNoMember] = useState(null);

  const [identifyLock, setIdentifyLock] = useState(false);

  // v8: 단일 boolean. lastChangeCategory 는 hook 내부 부가정보
  const [hasUnacknowledgedChanges, setHasUnacknowledgedChanges] = useState(false);
  const [lastChangeCategory, setLastChangeCategory] = useState(null);

  // in-flight 가드 (§5-1 hasPendingWriteAction)
  const pendingActionsRef = useRef(new Set());
  const beginAction = useCallback((id) => { pendingActionsRef.current.add(id); }, []);
  const endAction = useCallback((id) => { pendingActionsRef.current.delete(id); }, []);
  const hasPendingWriteAction = useCallback(() => pendingActionsRef.current.size > 0, []);

  const markDirty = useCallback((category) => {
    setHasUnacknowledgedChanges(true);
    setLastChangeCategory(category);
  }, []);

  const acknowledgeAndReset = useCallback(() => {
    setHasUnacknowledgedChanges(false);
    setLastChangeCategory(null);
  }, []);

  // 식별이 바뀌면 자동 reset (§5-3-c)
  const lastMemberIdRef = useRef(null);
  useEffect(() => {
    const id = primary?.member_id || scannedMember?.member_id || null;
    if (id !== lastMemberIdRef.current) {
      lastMemberIdRef.current = id;
      setHasUnacknowledgedChanges(false);
      setLastChangeCategory(null);
    }
  }, [primary, scannedMember]);

  const reset = useCallback(() => {
    setScannedMember(null);
    setPrimary(null);
    setFamily([]);
    setPassportMap({});
    setError(null);
    setTicketNoMember(null);
    setHasUnacknowledgedChanges(false);
    setLastChangeCategory(null);
  }, []);

  // 단일 진입 — race 차단
  const runIdentify = useCallback(
    async (loader) => {
      if (identifyLock || loading) return null;
      setIdentifyLock(true);
      setLoading(true);
      setError(null);
      setScannedMember(null);
      setPrimary(null);
      setFamily([]);
      setPassportMap({});
      setTicketNoMember(null);
      try {
        const member = await loader();
        if (!member) return null;
        setScannedMember(member);
        const { family: fam, primary: par, passportMap: pmap } = await loadFamilyAndPassports(member);
        setFamily(fam);
        setPrimary(par);
        setPassportMap(pmap);
        return member;
      } catch (err) {
        setError(err?.message || '회원 정보를 조회할 수 없습니다');
        return null;
      } finally {
        setLoading(false);
        setIdentifyLock(false);
      }
    },
    [identifyLock, loading],
  );

  const identifyByQR = useCallback(
    (rawCode) => runIdentify(async () => {
      const extracted = extractQRCode(rawCode);
      const code = extracted || rawCode;
      if (!isOurQR(code)) {
        // 티켓으로 폴백
        const ticketRes = await getMemberByTicket(code);
        if (!ticketRes?.found) { setError('등록되지 않은 티켓입니다'); return null; }
        if (!ticketRes?.has_member) {
          setTicketNoMember({ ticketId: code, message: ticketRes.message });
          return null;
        }
        return ticketRes.member;
      }
      const memberRes = await getMemberByQR(code);
      return memberRes?.member || null;
    }),
    [runIdentify],
  );

  const identifyByPhone = useCallback(
    (digits8) => runIdentify(async () => {
      if (!/^\d{8}$/.test(digits8)) { setError('전화번호 뒤 8자리를 입력해주세요'); return null; }
      const phone = '010' + digits8;
      try {
        const memberRes = await getMemberByPhone(phone);
        return memberRes?.member || null;
      } catch (err) {
        // 404 → 미등록 → 호출자가 신규가입 분기
        if (err?.status === 404 || /404/.test(err?.message || '')) {
          setError('미등록 회원');
          return null;
        }
        throw err;
      }
    }),
    [runIdentify],
  );

  const identifyByTicket = useCallback(
    (ticketId) => runIdentify(async () => {
      const ticketRes = await getMemberByTicket(ticketId);
      if (!ticketRes?.found) { setError('등록되지 않은 티켓입니다'); return null; }
      if (!ticketRes?.has_member) {
        setTicketNoMember({ ticketId, message: ticketRes.message });
        return null;
      }
      return ticketRes.member;
    }),
    [runIdentify],
  );

  const refresh = useCallback(async () => {
    const target = scannedMember;
    if (!target) return;
    try {
      const { family: fam, primary: par, passportMap: pmap } = await loadFamilyAndPassports(target);
      setFamily(fam);
      setPrimary(par);
      setPassportMap(pmap);
    } catch (_) {
      // 무시 — UI 측 토스트는 호출자가
    }
  }, [scannedMember]);

  return {
    scannedMember,
    primary,
    family,
    passportMap,
    loading,
    identifyLock,
    error,
    ticketNoMember,

    identifyByQR,
    identifyByPhone,
    identifyByTicket,
    refresh,
    reset,

    hasUnacknowledgedChanges,
    lastChangeCategory,
    markDirty,
    acknowledgeAndReset,

    pendingActions: pendingActionsRef.current,
    beginAction,
    endAction,
    hasPendingWriteAction,
  };
}
